/**
 * Shared consent-cookie contract (plan/09 §2, plan/06 §consent_log). Pure,
 * environment-agnostic helpers used by BOTH the client (`ConsentBanner.svelte`,
 * root layout) and the no-JS form action (`routes/consent/+page.server.ts`), so
 * the cookie shape and the `/api/consent` payload can never drift between the
 * two paths. `read`/`writeConsentCookie` touch `document` and are browser-only;
 * `parse`/`serializeConsentCookie` are plain string↔object transforms safe to
 * call from server code (SvelteKit's `cookies.get()` string in, `cookies.set()`
 * string out).
 */

export const CONSENT_COOKIE = 'ppf_consent';
/** Bump when the banner's copy or choice model changes materially. */
export const BANNER_VERSION = 'v3';
/** 12 months — matches plan/09 §1's stated lifetime for the consent choice itself. */
export const CONSENT_COOKIE_MAX_AGE = 60 * 60 * 24 * 365;

export function consentChoices(choice: unknown): { analytics: boolean; advertising: boolean } {
	return { analytics: choice === 'all' || choice === 'analytics', advertising: choice === 'all' };
}

export interface ConsentCookie {
	v: 1;
	analytics: boolean;
	advertising?: boolean;
	banner_version?: string;
	ts: number;
	/** Random UUID, minted once (client or the no-JS form action) — no fingerprinting. */
	visitor_id: string;
}

export function serializeConsentCookie(value: ConsentCookie): string {
	return JSON.stringify(value);
}

/** Parses + validates a raw cookie value; returns `null` for anything malformed
 * rather than throwing, so a tampered/stale cookie just re-prompts the banner. */
export function parseConsentCookie(raw: string | undefined | null): ConsentCookie | null {
	if (!raw) return null;
	try {
		const parsed: unknown = JSON.parse(raw);
		if (
			parsed &&
			typeof parsed === 'object' &&
			(parsed as Partial<ConsentCookie>).v === 1 &&
			typeof (parsed as Partial<ConsentCookie>).analytics === 'boolean' &&
			typeof (parsed as Partial<ConsentCookie>).visitor_id === 'string' &&
			typeof (parsed as Partial<ConsentCookie>).ts === 'number'
		) {
			const value = parsed as ConsentCookie;
			if (
				!Number.isFinite(value.ts) ||
				value.ts > Date.now() + 60_000 ||
				Date.now() - value.ts > CONSENT_COOKIE_MAX_AGE * 1000
			)
				return null;
			return {
				...value,
				advertising: value.banner_version === BANNER_VERSION && value.advertising === true
			};
		}
		return null;
	} catch {
		return null;
	}
}

/** Reads + parses the consent cookie from `document.cookie`. Browser-only. */
export function readConsentCookie(): ConsentCookie | null {
	const match = document.cookie.match(/(?:^|;\s*)ppf_consent=([^;]*)/);
	if (!match) return null;
	try {
		return parseConsentCookie(decodeURIComponent(match[1]));
	} catch {
		return null;
	}
}

/** Writes the consent cookie via `document.cookie`. Browser-only.
 * `SameSite=Lax` (no `Secure` flag) so it still sets over plain-http localhost
 * during dev — this is a preference cookie, not a session/auth token. */
export function shouldClearTrackingCookie(name: string, value: ConsentCookie): boolean {
	return (
		(!value.analytics &&
			(name === 'ppfe_attribution' || /^_ga(?:_|$)/.test(name) || /^_g(?:id|at)$/.test(name))) ||
		(!value.advertising && (name === '_fbp' || name === '_fbc' || /^_gcl_/.test(name)))
	);
}

export function cookieDomains(hostname: string): string[] {
	const labels = hostname.split('.');
	return ['', ...labels.slice(0, -1).map((_, index) => `.${labels.slice(index).join('.')}`)];
}

export function writeConsentCookie(value: ConsentCookie): void {
	const encoded = encodeURIComponent(serializeConsentCookie(value));
	document.cookie = `${CONSENT_COOKIE}=${encoded}; path=/; max-age=${CONSENT_COOKIE_MAX_AGE}; samesite=lax`;
	window.gtag?.('consent', 'update', {
		analytics_storage: value.analytics ? 'granted' : 'denied',
		ad_storage: value.advertising ? 'granted' : 'denied',
		ad_user_data: value.advertising ? 'granted' : 'denied',
		ad_personalization: value.advertising ? 'granted' : 'denied'
	});
	if (!value.advertising) window.fbq?.('consent', 'revoke');
	if (!value.analytics || !value.advertising) {
		for (const cookie of document.cookie.split(';')) {
			const name = cookie.split('=', 1)[0]?.trim();
			if (shouldClearTrackingCookie(name, value)) {
				for (const domain of cookieDomains(location.hostname)) {
					document.cookie = `${name}=; path=/; max-age=0; samesite=lax${domain ? `; domain=${domain}` : ''}`;
				}
			}
		}
	}
}

/** Reuses the visitor_id from an existing cookie, or mints a fresh UUID. Browser-only. */
export function ensureVisitorId(existing: ConsentCookie | null): string {
	return existing?.visitor_id ?? crypto.randomUUID();
}

/** POSTs the consent choice to the logging endpoint (plan/06 §consent_log).
 * Best-effort — never throws, so a briefly-unreachable PocketBase never blocks
 * the user's choice from taking effect locally. Browser-only (uses `fetch`
 * against a relative URL); the no-JS server action performs the equivalent
 * write itself via `event.fetch` — see routes/consent/+page.server.ts. */
export async function postConsent(
	visitorId: string,
	analytics: boolean,
	advertising = false
): Promise<void> {
	try {
		await fetch('/api/consent', {
			method: 'POST',
			headers: { 'content-type': 'application/json' },
			body: JSON.stringify({
				visitor_id: visitorId,
				banner_version: BANNER_VERSION,
				choices: { analytics, advertising }
			})
		});
	} catch {
		// Best-effort logging only — see doc comment above.
	}
}
